import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from '../../lib/toast';
import { API, auth, PageHead, errMsg, fmtDate } from '../organization/finance/shared';

const REQ_STATUS_LABELS = { draft: 'مسودة', sent: 'مرسل', viewed: 'تمت المشاهدة', accepted_contact: 'تم قبول التواصل', rejected: 'مرفوض', interview_requested: 'طلب مقابلة', interview_scheduled: 'مقابلة مجدولة', offer_sent: 'تم إرسال عرض', offer_accepted: 'تم قبول العرض', offer_rejected: 'تم رفض العرض', hired: 'تم التعاقد', cancelled: 'ملغي', expired: 'منتهي' };
const OFFER_STATUS_LABELS = { sent: 'مرسل', accepted: 'مقبول', rejected: 'مرفوض', cancelled: 'ملغي', expired: 'منتهي' };
const STATUS_COLORS = { sent: 'bg-blue-50 text-blue-700', viewed: 'bg-blue-50 text-blue-700', accepted_contact: 'bg-teal-50 text-teal-700', accepted: 'bg-teal-50 text-teal-700', rejected: 'bg-red-50 text-red-600', hired: 'bg-teal-50 text-teal-700', cancelled: 'bg-slate-100 text-slate-500', expired: 'bg-slate-100 text-slate-500' };

export default function CoachRequestsPage() {
  const [tab, setTab] = useState('requests');
  const [requests, setRequests] = useState([]);
  const [offers, setOffers] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, o, c] = await Promise.all([
        axios.get(`${API}/coaches/me/requests`, auth()),
        axios.get(`${API}/coaches/me/offers`, auth()),
        axios.get(`${API}/coaches/profile/me/contact-messages`, auth()),
      ]);
      setRequests(r.data); setOffers(o.data); setContactMessages(c.data);
    } catch { setRequests([]); setOffers([]); setContactMessages([]); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const respondRequest = async (id, status) => {
    try { await axios.put(`${API}/coaches/me/requests/${id}/status`, { status }, auth()); toast.success('تم التحديث'); load(); }
    catch (err) { toast.error(errMsg(err, 'فشل التحديث')); }
  };

  const respondOffer = async (id, accept) => {
    try { await axios.put(`${API}/coaches/me/offers/${id}/respond?accept=${accept}`, {}, auth()); toast.success(accept ? 'تم قبول العرض' : 'تم رفض العرض'); load(); }
    catch (err) { toast.error(errMsg(err, 'فشل التحديث')); }
  };

  const markMessageRead = async (id) => {
    try { await axios.put(`${API}/coaches/profile/me/contact-messages/${id}/mark-read`, {}, auth()); load(); }
    catch (err) { toast.error(errMsg(err, 'فشل التحديث')); }
  };

  return (
    <div className="space-y-6" data-testid="coach-requests-page">
      <PageHead title="الطلبات والعروض" desc="طلبات التواصل والعروض الواردة من المنشآت، ورسائل التواصل العامة من زوار الموقع" />

      <div className="flex flex-wrap gap-1.5 overflow-x-auto rounded-2xl border border-slate-200/70 bg-white p-1.5 shadow-[0_1px_3px_rgba(0,0,0,0.04)] w-fit max-w-full">
        <button onClick={() => setTab('requests')} data-testid="coach-tab-requests" className={`whitespace-nowrap rounded-xl px-4 py-2.5 text-sm font-bold transition-all active:scale-95 ${tab === 'requests' ? 'bg-gradient-to-br from-teal-500 to-emerald-600 text-white shadow-md shadow-teal-500/20' : 'text-slate-500 hover:bg-slate-50'}`}>طلبات التواصل ({requests.length})</button>
        <button onClick={() => setTab('offers')} data-testid="coach-tab-offers" className={`whitespace-nowrap rounded-xl px-4 py-2.5 text-sm font-bold transition-all active:scale-95 ${tab === 'offers' ? 'bg-gradient-to-br from-teal-500 to-emerald-600 text-white shadow-md shadow-teal-500/20' : 'text-slate-500 hover:bg-slate-50'}`}>العروض ({offers.length})</button>
        <button onClick={() => setTab('contact_messages')} data-testid="coach-tab-contact-messages" className={`whitespace-nowrap rounded-xl px-4 py-2.5 text-sm font-bold transition-all active:scale-95 ${tab === 'contact_messages' ? 'bg-gradient-to-br from-teal-500 to-emerald-600 text-white shadow-md shadow-teal-500/20' : 'text-slate-500 hover:bg-slate-50'}`}>رسائل تواصل عامة ({contactMessages.length})</button>
      </div>

      {loading ? <p className="text-sm text-slate-400">جارِ التحميل...</p> : tab === 'requests' ? (
        requests.length === 0 ? <p className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 py-10 text-center text-sm text-slate-400" data-testid="coach-requests-empty">لا توجد طلبات تواصل حالياً</p> : (
          <div className="space-y-3">
            {requests.map((r) => (
              <div key={r.id} className="rounded-2xl border border-slate-200/70 bg-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid="coach-request-row">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-sm font-bold text-slate-800">{r.club_name}</div>
                    <div className="text-xs text-slate-500">{r.position_title || 'فرصة تدريب'} — {fmtDate(r.created_at)}</div>
                    {r.message && <p className="mt-1 text-sm text-slate-600">{r.message}</p>}
                  </div>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_COLORS[r.status] || 'bg-slate-100 text-slate-500'}`}>{REQ_STATUS_LABELS[r.status] || r.status}</span>
                </div>
                {(r.status === 'sent' || r.status === 'viewed') && (
                  <div className="mt-3 flex gap-2">
                    <button onClick={() => respondRequest(r.id, 'accepted_contact')} className="rounded-lg bg-teal-600 px-4 py-1.5 text-xs font-bold text-white" data-testid="coach-request-accept-btn">قبول التواصل</button>
                    <button onClick={() => respondRequest(r.id, 'rejected')} className="rounded-lg border border-slate-200 px-4 py-1.5 text-xs font-semibold text-slate-600" data-testid="coach-request-reject-btn">رفض</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      ) : tab === 'offers' ? (
        offers.length === 0 ? <p className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 py-10 text-center text-sm text-slate-400" data-testid="coach-offers-empty">لا توجد عروض حالياً</p> : (
          <div className="space-y-3">
            {offers.map((o) => (
              <div key={o.id} className="rounded-2xl border border-slate-200/70 bg-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid="coach-offer-row">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-sm font-bold text-slate-800">{o.title} — {o.club_name}</div>
                    <div className="text-xs text-slate-500">{o.season && `الموسم: ${o.season} · `}يبدأ {fmtDate(o.start_date)}</div>
                    {o.terms_note && <p className="mt-1 text-sm text-slate-600">{o.terms_note}</p>}
                  </div>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_COLORS[o.status] || 'bg-slate-100 text-slate-500'}`}>{OFFER_STATUS_LABELS[o.status] || o.status}</span>
                </div>
                {o.status === 'sent' && (
                  <div className="mt-3 flex gap-2">
                    <button onClick={() => respondOffer(o.id, true)} className="rounded-lg bg-teal-600 px-4 py-1.5 text-xs font-bold text-white" data-testid="coach-offer-accept-btn">قبول العرض</button>
                    <button onClick={() => respondOffer(o.id, false)} className="rounded-lg border border-slate-200 px-4 py-1.5 text-xs font-semibold text-slate-600" data-testid="coach-offer-reject-btn">رفض العرض</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      ) : (
        contactMessages.length === 0 ? <p className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 py-10 text-center text-sm text-slate-400" data-testid="coach-contact-messages-empty">لا توجد رسائل تواصل عامة حالياً</p> : (
          <div className="space-y-3">
            {contactMessages.map((m) => (
              <div key={m.id} className="rounded-2xl border border-slate-200/70 bg-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid="coach-contact-message-row">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-sm font-bold text-slate-800">{m.sender_name}{m.organization_name && ` — ${m.organization_name}`}</div>
                    <div className="text-xs text-slate-500" dir="ltr">{m.contact_info} · {fmtDate(m.created_at)}</div>
                    <p className="mt-1 text-sm text-slate-600">{m.message}</p>
                  </div>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${m.status === 'read' ? 'bg-slate-100 text-slate-500' : 'bg-amber-50 text-amber-700'}`}>{m.status === 'read' ? 'مقروءة' : 'جديدة'}</span>
                </div>
                {m.status !== 'read' && (
                  <button onClick={() => markMessageRead(m.id)} className="mt-3 rounded-lg border border-slate-200 px-4 py-1.5 text-xs font-semibold text-slate-600" data-testid="coach-contact-message-mark-read-btn">تمييز كمقروءة</button>
                )}
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
